// src/pages/payment.js — PhonePe redirect-based payment flow

import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import api from '../store/api/base';
import * as paymentApi from '../store/api/payment';
import Loader from '../components/common/loader';
import OrderSummary from '../components/checkout/orderSummary';
import { setError, setSuccess } from '../store/slices/uiSlice';
import { createPaymentOrder, resetPayment } from '../store/slices/paymentSlice';

const Payment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const paymentInitiated = useRef(false);

  const [orderDetails, setOrderDetails] = React.useState(null);
  const [fetchingOrder, setFetchingOrder] = React.useState(true);
  const [paymentCancelled, setPaymentCancelled] = React.useState(false);

  const { paymentDetails, loading, error } = useSelector((state) => state.payment);

  // Prevent back navigation
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      if (window.confirm('Are you sure you want to leave? Your payment is pending.')) {
        handlePaymentCancellation();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    fetchOrderDetails();  
    return () => { dispatch(resetPayment()); };
  }, [orderId]);

  useEffect(() => {
    if (error.createPayment) {
      dispatch(setError(error.createPayment));
      navigate('/checkout', { replace: true });
    }
  }, [error.createPayment]);

  const fetchOrderDetails = async () => {
    try {
      setFetchingOrder(true);
      const orderResponse = await api.get(`/orders/${orderId}`);
      const order = orderResponse.data.data.order;

      if (order.status === 'CANCELLED') {
        dispatch(setError('This order has been cancelled'));
        navigate('/checkout', { replace: true });
        return;
      }

      if (order.payment?.status === 'COMPLETED') {
        dispatch(setSuccess('Payment already completed for this order'));
        navigate(`/order-success/${orderId}`, { replace: true });
        return;
      }

      const pricing = order.pricing || {};

      
      const transformedOrder = {
        orderId: order.orderId || order._id,
        items: order.items || [],
        totalItems: (order.items || []).length,
        subtotal: pricing.subTotal || 0,
        bulkDiscount: pricing.bulkDiscount || 0,
        discount: pricing.discount || 0,
        shipping: { cost: pricing.shippingCost || 0 },
        shippingCost: pricing.shippingCost || 0,
        gstCalculation: { totalGst: pricing.gst || 0 },
        gst: pricing.gst || 0,
        totalAmount: pricing.totalAmount || 0,
        paymentMethod: order.payment?.method || 'PHONEPE',
        paymentStatus: order.payment?.status || 'PENDING',
        status: order.status,
        createdAt: order.createdAt,
        shippingAddress: order.shippingAddress,
        billingAddress: order.billingAddress
      };

      if (transformedOrder.totalAmount === 0) {
        dispatch(setError('Invalid order amount. Please contact support.'));
        navigate('/checkout', { replace: true });
        return;
      }

      setOrderDetails(transformedOrder);
      // dispatch(createPaymentOrder({ orderId }));
      if (!paymentInitiated.current) {
  paymentInitiated.current = true;
  dispatch(createPaymentOrder({ orderId }));
}
      setFetchingOrder(false);
    } catch (err) {
      // console.error('Error fetching order details:', err);
      dispatch(setError(err.response?.data?.message || 'Failed to fetch order details'));
      setFetchingOrder(false);
      navigate('/checkout', { replace: true });
    }
  };

  const calculateTotalAmount = () => {
  if (!orderDetails) return 0;
  if (orderDetails.totalAmount > 0) return orderDetails.totalAmount;
  return (orderDetails.subtotal || 0) + (orderDetails.shippingCost || 0) + (orderDetails.gst || 0);
};

  const handlePaymentCancellation = async () => {
    try {
      setPaymentCancelled(true);
      await paymentApi.cancelPayment(orderId, 'Payment cancelled by user');
      dispatch(setError('Payment was cancelled. Redirecting to checkout...'));
      setTimeout(() => navigate('/checkout', { replace: true }), 2000);
    } catch (err) {
      // console.error('Error cancelling payment:', err);
      navigate('/checkout', { replace: true });
    }
  };

  const handlePayment = () => {
    if (paymentCancelled) {
      dispatch(setError('Payment has been cancelled for this order'));
      return;
    }
    if (!paymentDetails?.redirectUrl) {
      dispatch(setError('Payment gateway not ready. Please refresh and try again.'));
      return;
    }
    window.location.href = paymentDetails.redirectUrl;
  };

  const handleCancelOrder = async () => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      await handlePaymentCancellation();
    }
  };

  const isLoading = fetchingOrder || loading.createPayment;

  if (isLoading && !orderDetails) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <Loader size="lg" />
          <p className="mt-4 text-gray-600">
            {loading.createPayment && 'Preparing payment...'}
            {fetchingOrder && 'Loading order details...'}
          </p>
        </div>
      </div>
    );
  }

  if (paymentCancelled) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold mb-2">Payment Cancelled</h2>
          <p className="text-gray-600 mb-4">Your order has been cancelled.</p>
          <Loader size="sm" />
          <p className="text-sm text-gray-500 mt-2">Redirecting to checkout page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Complete Your Payment</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {orderDetails && <OrderSummary summary={orderDetails} showPaymentStatus={false} />}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 lg:sticky lg:top-20">
              <h2 className="text-xl font-semibold mb-4">Payment Details</h2>

              {orderDetails && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3 pb-3 border-b">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="font-medium text-sm">{orderDetails.orderId}</span>
                  </div>
                  <div className="flex justify-between items-center mb-4 pb-4 border-b">
                    <span className="text-lg font-semibold">Amount to Pay:</span>
                    <span className="text-2xl font-bold text-purple-600">
                      ₹{calculateTotalAmount().toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-2">
                    <div className="flex justify-between">
                      <span>Items:</span>
                      <span className="font-medium">{orderDetails.totalItems}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center">
                <img
                  src="/assets/payment/PhonePe-Logo.wine.svg"
                  alt="PhonePe"
                  className="h-14 mb-4"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />

                <p className="text-xs text-gray-500 mb-4 text-center">
                  You will be redirected to PhonePe's secure payment page to complete your payment.
                </p>

                <button
                  className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                  onClick={handlePayment}
                  disabled={isLoading || !paymentDetails?.redirectUrl || calculateTotalAmount() === 0 || paymentCancelled}
                >
                  {loading.createPayment ? (
                    <><Loader size="sm" className="mr-2" /> Preparing Payment...</>
                  ) : (
                    <>Pay ₹{calculateTotalAmount().toLocaleString('en-IN')} via PhonePe</>
                  )}
                </button>

                <button
                  className="mt-4 w-full text-red-600 hover:text-red-700 border border-red-600 hover:border-red-700 py-2 rounded-lg text-sm font-medium transition"
                  onClick={handleCancelOrder}
                  disabled={paymentCancelled}
                >
                  Cancel Order
                </button>

                <button
                  className="mt-4 text-purple-600 hover:underline text-sm"
                  onClick={() => navigate('/checkout', { replace: true })}
                >
                  ← Back to Checkout
                </button>

                <div className="mt-6 text-xs text-gray-500 text-center">
                  <p className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Secure payment powered by PhonePe
                  </p>
                  <p className="mt-1">Your payment information is encrypted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;